const STORAGE_KEY = 'salesbreaker:account:addon-mutation-idempotency';
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{15,127}$/;
const MAX_STORED_INTENTS = 16;

export const ADDON_MUTATION_PATHS = Object.freeze({
    add_job_slot: '/api/subscriptions/add-job-slot',
    remove_job_slot: '/api/subscriptions/remove-job-slot',
    add_marunage: '/api/subscriptions/add-marunage-option',
    remove_marunage: '/api/subscriptions/remove-marunage-option',
});

const inMemoryKeys = new Map();

export const buildAddonMutationIntent = ({
    operation,
    subscriptionId,
}) => {
    if (!ADDON_MUTATION_PATHS[operation]) {
        throw new Error('Unsupported add-on mutation operation.');
    }

    const intent = {
        operation,
        subscription_id: String(subscriptionId || '').trim(),
    };
    if (!intent.subscription_id) {
        throw new Error('Subscription ID is required.');
    }
    return intent;
};

const serializeIntent = (intent) => JSON.stringify(intent);

const normalizeStoredEntry = (entry) => {
    if (!IDEMPOTENCY_KEY_PATTERN.test(String(entry?.idempotency_key || ''))) return null;
    try {
        const storedIntent = JSON.parse(entry?.intent);
        const intent = buildAddonMutationIntent({
            operation: storedIntent?.operation,
            subscriptionId: storedIntent?.subscription_id,
        });
        return {
            intent: serializeIntent(intent),
            idempotency_key: String(entry.idempotency_key),
        };
    } catch (_error) {
        return null;
    }
};

const resolveStorage = (storage) => {
    if (storage) return storage;
    try {
        return window.sessionStorage;
    } catch (_error) {
        return null;
    }
};

const readEntries = (storage) => {
    if (!storage) return [];
    try {
        const entries = JSON.parse(storage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(entries)) return [];
        return entries.reduce((normalizedEntries, entry) => {
            const normalizedEntry = normalizeStoredEntry(entry);
            if (!normalizedEntry) return normalizedEntries;
            return [
                ...normalizedEntries.filter((candidate) => (
                    candidate.intent !== normalizedEntry.intent
                )),
                normalizedEntry,
            ];
        }, []);
    } catch (_error) {
        return [];
    }
};

const writeEntries = (storage, entries) => {
    if (!storage) return;
    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_STORED_INTENTS)));
    } catch (_error) {
        // The in-memory copy still protects retries during this page session.
    }
};

const createIdempotencyKey = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

export const getOrCreateAddonMutationKey = (intentInput, storage) => {
    const intent = buildAddonMutationIntent(intentInput);
    const serializedIntent = serializeIntent(intent);
    const memoryKey = inMemoryKeys.get(serializedIntent);
    if (IDEMPOTENCY_KEY_PATTERN.test(String(memoryKey || ''))) return memoryKey;

    const resolvedStorage = resolveStorage(storage);
    const entries = readEntries(resolvedStorage);
    const storedEntry = entries.find((entry) => entry.intent === serializedIntent);
    if (storedEntry) {
        inMemoryKeys.set(serializedIntent, storedEntry.idempotency_key);
        return storedEntry.idempotency_key;
    }

    const idempotencyKey = createIdempotencyKey();
    inMemoryKeys.set(serializedIntent, idempotencyKey);
    writeEntries(resolvedStorage, [
        ...entries.filter((entry) => entry.intent !== serializedIntent),
        { intent: serializedIntent, idempotency_key: idempotencyKey },
    ]);
    return idempotencyKey;
};

export const clearAddonMutationKey = (intentInput, storage) => {
    const intent = buildAddonMutationIntent(intentInput);
    const serializedIntent = serializeIntent(intent);
    inMemoryKeys.delete(serializedIntent);

    const resolvedStorage = resolveStorage(storage);
    const entries = readEntries(resolvedStorage);
    writeEntries(
        resolvedStorage,
        entries.filter((entry) => entry.intent !== serializedIntent),
    );
};

export const getPendingAddonMutation = (storage, subscriptionId = null) => {
    const resolvedStorage = resolveStorage(storage);
    const expectedSubscriptionId = subscriptionId == null
        ? null
        : String(subscriptionId).trim();
    const entries = readEntries(resolvedStorage);

    for (let index = entries.length - 1; index >= 0; index -= 1) {
        try {
            const intent = JSON.parse(entries[index].intent);
            if (!ADDON_MUTATION_PATHS[intent?.operation]) continue;
            if (!intent?.subscription_id) continue;
            if (
                expectedSubscriptionId
                && String(intent.subscription_id) !== expectedSubscriptionId
            ) continue;
            return {
                ...intent,
                idempotency_key: entries[index].idempotency_key,
            };
        } catch (_error) {
            // Ignore malformed entries and continue looking for a usable pending intent.
        }
    }
    return null;
};

export const requestAddonMutation = async ({
    apiUrl,
    operation,
    subscriptionId,
    authHeaders = {},
    fetchImpl,
    storage,
}) => {
    const intentInput = {
        operation,
        subscriptionId,
    };
    const idempotencyKey = getOrCreateAddonMutationKey(intentInput, storage);
    const body = {
        subscriptionId,
        idempotency_key: idempotencyKey,
    };
    const request = fetchImpl || window.fetch;

    let response;
    try {
        response = await request(`${apiUrl}${ADDON_MUTATION_PATHS[operation]}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders,
                'Idempotency-Key': idempotencyKey,
            },
            body: JSON.stringify(body),
        });
    } catch (networkError) {
        // The server may have accepted the request. Preserve this exact intent and key.
        const requestError = networkError instanceof Error
            ? networkError
            : new Error('オプションの更新結果を確認できませんでした。');
        requestError.retryWithSameIntent = true;
        requestError.addonMutationIntent = buildAddonMutationIntent(intentInput);
        throw requestError;
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success !== true) {
        const isTerminalWithoutSideEffects = data.terminal === true
            && data.no_side_effect === true;
        const shouldKeepSameKey = !isTerminalWithoutSideEffects;
        if (!shouldKeepSameKey) {
            clearAddonMutationKey(intentInput, storage);
        }
        const requestError = new Error(data.error || 'オプションの更新に失敗しました。');
        requestError.status = response.status;
        requestError.retryWithSameIntent = shouldKeepSameKey;
        requestError.addonMutationIntent = buildAddonMutationIntent(intentInput);
        throw requestError;
    }

    clearAddonMutationKey(intentInput, storage);
    return data;
};
